import React, {lazy, Suspense} from "react";
import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import {useFlag} from "@openfeature/react-sdk";
import {usePermissions} from "./context/AuthzProvider";
import {useOrgSettings} from "./context/OrgSettingsProvider";
import {useHome} from "./context/HomeProvider";
import IntegrationRoutes from "./pages/Integrations";

const ObligationRedirect = () => {
  const {pathname, search, hash} = useLocation();
  return (
    <Navigate to={pathname.replace(/^\/obligations/, "/procedures") + search + hash} replace />
  );
};

const AsyncLandingPage = lazy(() => import("./pages/LandingPage/LandingPage"));
const AsyncMyActionsPage = lazy(() => import("./pages/MyActions"));
const AsyncIncidentRoutes = lazy(() => import("./pages/Incidents"));
const AsyncExerciseRoutes = lazy(() => import("./pages/Exercises"));
const AsyncObligationRoutes = lazy(() => import("./pages/Obligations"));
const AsyncTaskRoutes = lazy(() => import("./pages/Tasks"));
const AsyncPlaybookRoutes = lazy(() => import("./pages/Playbooks"));
const AsyncPlayground = lazy(() => import("./pages/Playground/Playground"));
const AsyncReports = lazy(() => import("./pages/Reports"));
const AsyncMessaging = lazy(() => import("./pages/Messaging"));
const AsyncRexAI = lazy(() => import("./pages/RexAITrustedAdvisor"));
const AsyncNews = lazy(() => import("./pages/News/News"));
const AsyncSchemaRoutes = lazy(() => import("./pages/Schema"));
const AsyncUserRoutes = lazy(() => import("./pages/Users"));
const AsyncRoleRoutes = lazy(() => import("./pages/Roles"));
const AsyncPermissionRoutes = lazy(() => import("./pages/Permissions"));
const AsyncOrganization = lazy(() => import("./pages/Organization"));
const AsyncNotifications = lazy(() => import("./pages/Notifications"));
const AsyncNotificationTemplateRoutes = lazy(() => import("./pages/NotificationTemplates"));
const AsyncDatasets = lazy(() => import("./pages/Datasets"));
const AsyncApiKeys = lazy(() => import("./pages/ApiKeys"));
const AsyncUserProfile = lazy(() => import("./pages/UserProfile/UserProfile"));
const AsyncComplianceRoutes = lazy(() => import("./pages/Compliance"));
const AsyncEvidenceRoutes = lazy(() => import("./pages/Evidence"));
const AsyncReportTemplateRoutes = lazy(() => import("./pages/ReportTemplates"));
const AsyncHelpGuides = lazy(() => import("./pages/HelpGuides/HelpGuides"));
const AsyncLibraryRoutes = lazy(() => import("./pages/Library"));

const PlatformRoutes = () => {
  const {
    canAccessAuditSummary,
    canAccessDocumentLibrary,
    canAccessExercise,
    canAccessIncident,
    canAccessExerciseOrIncident,
    canAccessHelpGuides,
    canAccessMessage,
    canManageApiKey,
    canManageDataOption,
    canManageDataset,
    canManageIntegration,
    canManageNotification,
    canManageObligation,
    canManageOrganization,
    canManagePermission,
    canManagePlaybook,
    canManageReportTemplate,
    canManageRole,
    canManageTask,
    canManageUser,
    canReadDashboard,
    canReadIncidentSummary,
    canReadIncidentAuditLog,
    canReadNews,
    canReadResponsePlan,
    canReadRiskAssessment,
    canAccessTrustedAdvisor,
  } = usePermissions();
  const {responseWorkflowName, showRegulatoryResearch} = useOrgSettings();
  const {home} = useHome();
  const {value: enableCustomNotificationTemplatesUi} =
    useFlag("enableCustomNotificationTemplatesUi", false);

  const storedRedirectPath = window.sessionStorage.getItem("redirectPath");
  if (window.location.pathname === storedRedirectPath) {
    window.sessionStorage.removeItem("redirectPath");
  }
  const redirectPath = storedRedirectPath || home;

  return (
    <Suspense fallback={null}>
      <Routes>
        {canReadDashboard &&
          canAccessExerciseOrIncident && [
          <Route
            path="/dashboardold"
            element={<Navigate to="/dashboard" replace />}
            key="dashboardold"
          />,
          <Route path="/dashboard" element={<AsyncLandingPage />} key="dashboard" />,
        ]}
        {canAccessExerciseOrIncident && (
          <Route path="/actions/*" element={<AsyncMyActionsPage />} />
        )}
        {canAccessIncident && [
          <Route
            path={`/${responseWorkflowName.lower}s/*`}
            element={<AsyncIncidentRoutes />}
            key="incident"
          />,
          <Route path="/workflow/*" element={<AsyncIncidentRoutes />} key="workflow" />,
        ]}
        {canAccessExercise && <Route path="/exercises/*" element={<AsyncExerciseRoutes />} />}
        {canAccessExerciseOrIncident && (
          <Route path="/evidence/*" element={<AsyncEvidenceRoutes />} />
        )}

        {(canAccessAuditSummary || showRegulatoryResearch || canReadResponsePlan) && (
          <Route path="/compliance/*" element={<AsyncComplianceRoutes />} />
        )}

        {canAccessDocumentLibrary && <Route path="/library/*" element={<AsyncLibraryRoutes />} />}

        {(canReadRiskAssessment ||
          canReadIncidentSummary ||
          canReadIncidentAuditLog ||
          canAccessExerciseOrIncident) && <Route path="/reports/*" element={<AsyncReports />} />}

        {canAccessTrustedAdvisor && [
          <Route key="maestro" path="/rex-ai-maestro" element={<AsyncRexAI />} />,
          <Route
            key="maesto-redirect"
            path="/rex-ai-trusted-advisor"
            element={<Navigate to="/rex-ai-maestro" replace />}
          />,
        ]}

        {canAccessMessage && <Route path="/messaging/*" element={<AsyncMessaging />} />}
        {canReadNews && <Route path="/news/*" element={<AsyncNews />} />}

        {canManageTask && <Route path="/tasks/*" element={<AsyncTaskRoutes />} />}
        {canManageObligation && [
          <Route path="/procedures/*" element={<AsyncObligationRoutes />} key="procedures" />,
          <Route
            path="/obligations/*"
            element={<ObligationRedirect />}
            key="obligations-redirect"
          />,
        ]}
        {canManagePlaybook && <Route path="/playbooks/*" element={<AsyncPlaybookRoutes />} />}
        {canManageReportTemplate && (
          <Route path="/reportTemplates/*" element={<AsyncReportTemplateRoutes />} />
        )}
        {canManageDataset && <Route path="/datasets/*" element={<AsyncDatasets />} />}
        {canManageDataOption && [
          <Route path="/dataOptions/*" element={<AsyncSchemaRoutes />} key="dataOption" />,
          <Route path="/data/*" element={<AsyncSchemaRoutes />} key="data" />,
          <Route path="/schema/*" element={<AsyncSchemaRoutes />} key="schema" />,
        ]}

        {canManageUser && <Route path="/users/*" element={<AsyncUserRoutes />} />}
        {canManageRole && <Route path="/roles/*" element={<AsyncRoleRoutes />} />}
        {canManagePermission && <Route path="/permissions/*" element={<AsyncPermissionRoutes />} />}
        {canManageOrganization && <Route path="/organization/*" element={<AsyncOrganization />} />}
        {canManageNotification && (
          <Route path="/notifications/*" element={<AsyncNotifications />} />
        )}
        {canManageNotification && enableCustomNotificationTemplatesUi && (
          <Route path="/notificationTemplates/*" element={<AsyncNotificationTemplateRoutes />} />
        )}

        {canManageIntegration && <Route path="/integrations/*" element={<IntegrationRoutes />} />}
        {canManageApiKey && <Route path="/api/*" element={<AsyncApiKeys />} />}

        <Route path="/userProfile/*" element={<AsyncUserProfile />} />
        {canAccessHelpGuides && <Route path="/helpGuides/*" element={<AsyncHelpGuides />} />}
        <Route path="/playground/*" element={<AsyncPlayground />} />

        <Route path="*" element={<Navigate to={redirectPath} replace />} />
      </Routes>
    </Suspense>
  );
};

export default PlatformRoutes;
